import React from 'react';
import { Tag } from '../api/Tag';
import { useAuth } from '../context/AuthContext';
import useTagManager from '../hooks/tags/useTagManager';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormLabel,
  FormText,
  ListGroup,
  ListGroupAction,
  Switch,
  TextInput,
} from './ui';

interface TagManagerProps {
  availableTags: Tag[];
  selectedTagIds: number[];
  setAvailableTags: React.Dispatch<React.SetStateAction<Tag[]>>;
  setSelectedTagIds: React.Dispatch<React.SetStateAction<number[]>>;
  isAuthor: boolean; // To determine if delete button should be shown
  onMessage: (message: string, isError: boolean) => void; // Callback for messages
  submitting: boolean; // To disable buttons when form is submitting
}

const TagManager: React.FC<TagManagerProps> = ({
  availableTags,
  selectedTagIds,
  setAvailableTags,
  setSelectedTagIds,
  isAuthor,
  onMessage,
  submitting,
}) => {
  const { token } = useAuth();
  const {
    newTagName,
    setNewTagName,
    tagSuggestions,
    handleAddOrSelectTag,
    handleDeleteTag,
    handleSelectSuggestion,
    toggleTagSelection,
  } = useTagManager({
    availableTags,
    selectedTagIds,
    setAvailableTags,
    setSelectedTagIds,
    onMessage,
    token,
  });

  return (
    <>
      <Flex wrap marginBottom={2} role="group" aria-labelledby="tags-label">
        {availableTags.map(tag => {
          const isSelected = selectedTagIds.includes(tag.id);
          const tagId = `tag-switch-${tag.id}`; // Use a unique ID for each tag checkbox
          return (
            <Flex
              key={tag.id}
              align="center"
              padding={2}
              marginEnd={3}
              marginBottom={2}
              bordered
              borderColor="secondary"
              rounded
            >
              <Switch
                id={tagId}
                label={tag.name}
                checked={isSelected}
                disabled={!isAuthor}
                switchClassName="form-switch-secondary"
                labelClassName="text-secondary"
                onChange={() => {
                  if (isAuthor) {
                    toggleTagSelection(tag, isSelected);
                  }
                }}
              />
              {isAuthor && !isSelected && ( // Only allow deletion if user is author and tag is NOT selected
                <CloseButton
                  marginStart={2}
                  label={`Supprimer le tag ${tag.name}`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent checkbox/label click
                    handleDeleteTag(tag);
                  }}
                  disabled={submitting}
                />
              )}
            </Flex>
          );
        })}
      </Flex>

      {isAuthor && ( // Only author can add new tags
        <Box>
          <FormLabel htmlFor="newTagInput">Nouveau tag</FormLabel>
          <TextInput
            id="newTagInput"
            placeholder="Nouveau tag"
            aria-describedby="new-tag-help"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            disabled={submitting}
          />
          <FormText as="small" id="new-tag-help" marginBottom={2} textColor="muted">
            Suggérer un nouveau tag ou sélectionner un existant.
          </FormText>

          {tagSuggestions.length > 0 && (
            <ListGroup marginTop={2}>
              {tagSuggestions.map(tag => (
                <ListGroupAction
                  key={tag.id}
                  onClick={() => handleSelectSuggestion(tag)}
                  disabled={submitting}
                >
                  {tag.name}
                </ListGroupAction>
              ))}
            </ListGroup>
          )}

          <Button
            variant="secondary"
            outline
            marginTop={2}
            type="button"
            onClick={handleAddOrSelectTag}
            disabled={!newTagName.trim() || submitting}
          >
            Ajouter ou sélectionner le tag
          </Button>
        </Box>
      )}
    </>
  );
};

export default TagManager;
